import React from 'react';

import {
  CodeBlockType,
  ServerCodeLanguage,
  IOSCodeLanguage,
  AndroidCodeLanguage,
  FrontendCodeLanguage,
  ReactNativeiOSCodeLanguage,
} from '../types';
import { languageFromPrismClassName } from '../utilities';
import {
  SERVER_LANGUAGES,
  IOS_LANGUAGES,
  ANDROID_LANGUAGES,
  REACT_NATIVE_IOS_LANGUAGES,
  FRONTEND_LANGUAGES,
} from '../constants';
import CodeBlockV2, { CodeBlockTheme } from '../CodeBlockV2';

type LanguageVariant =
  | ServerCodeLanguage
  | IOSCodeLanguage
  | AndroidCodeLanguage
  | FrontendCodeLanguage
  | ReactNativeiOSCodeLanguage;

export interface Props {
  type: CodeBlockType;
  title?: string;
  children?: Array<React.ReactElement>;
  templateProps?: object;
  showLineNumbers?: boolean;
  disableCopy?: boolean;
  theme?: CodeBlockTheme;
  showLineStart?: number;
  showLineEnd?: number;
  maxLines?: number;
}

// replaceTemplateProps :: ( {{{ token }}} , { token: Any } ) -> String
// template string replacement given templateProps
const replaceTemplateProps = (s: string, props: object = {}) => {
  const pattern = /{{{(\w+?)}}}/g;
  return s.replace(pattern, (_, token) => props[token] || '');
};

const MDXCodeBlock: React.FC<Props> = (props) => {
  const {
    type = 'server',
    title,
    children,
    templateProps = {},
    showLineNumbers,
    disableCopy,
    theme,
    showLineStart,
    showLineEnd,
    maxLines,
  } = props;

  // Parse MDX children into variants object
  const variants: Partial<Record<LanguageVariant, string>> = {};
  let fernGoCode: string | undefined;

  if (!children || children.length === 0) {
    console.error('MDXCodeBlock: No children provided');
    return null;
  }

  children.forEach((child) => {
    const className = child.props.children?.props?.className;
    const codeContent = child.props.children?.props?.children;

    if (!className || !codeContent) {
      return;
    }

    const language = languageFromPrismClassName(className, type);
    const metadata = child.props.children?.props?.metastring || '';
    if (
      type === 'server' &&
      language === 'go' &&
      /(?:^|\s)sdk=fern(?:\s|$)/.test(metadata)
    ) {
      fernGoCode = replaceTemplateProps(codeContent, templateProps);
      return;
    }
    if (language) {
      // Apply template string replacement
      variants[language as LanguageVariant] = replaceTemplateProps(
        codeContent,
        templateProps,
      );
    }
  });

  // Validate all required languages are present (warning only)
  const typeToLanguagesMap: Record<CodeBlockType, Array<LanguageVariant>> = {
    server: SERVER_LANGUAGES as Array<LanguageVariant>,
    ios: IOS_LANGUAGES as Array<LanguageVariant>,
    android: ANDROID_LANGUAGES as Array<LanguageVariant>,
    react_native_ios: REACT_NATIVE_IOS_LANGUAGES as Array<LanguageVariant>,
    frontend: FRONTEND_LANGUAGES as Array<LanguageVariant>,
  };

  const requiredLanguages = typeToLanguagesMap[type];
  const providedLanguages = Object.keys(variants);

  const everyLanguagePresent = requiredLanguages.every((lang) =>
    providedLanguages.includes(lang),
  );

  if (!everyLanguagePresent) {
    const missing = requiredLanguages.filter(
      (lang) => !providedLanguages.includes(lang),
    );

    console.error(
      `MDXCodeBlock: Every ${type} code language should be represented in the example. Missing: ${missing.join(
        ', ',
      )}`,
    );
  }

  return (
    <CodeBlockV2
      variants={variants}
      fernGoCode={fernGoCode}
      type={type}
      title={title}
      showLineNumbers={showLineNumbers}
      disableCopy={disableCopy}
      theme={theme}
      showLineStart={showLineStart}
      showLineEnd={showLineEnd}
      maxLines={maxLines}
    />
  );
};

MDXCodeBlock.displayName = 'MDXCodeBlock';
export default MDXCodeBlock;
